// Viral-win detection — pure helpers for the "I just Nx'd this" share moment
// (MOR-540). A winning sell whose realized return multiple clears both gates
// (multiple AND a dollar floor) triggers the auto-prompted celebration share.
//
// All money is integer fixed-point (D6 USDC). The return multiple is a single
// scaled-bigint ratio converted to a number only for display thresholds — no
// float ever enters the money path.

import type { CheckedPositionAccountingResult } from '@morfi/market-math';

/** Minimum realized return multiple (proceeds ÷ cost of the sold portion) for
 *  the viral share to auto-prompt. 2 = "doubled your money". */
export const VIRAL_MIN_MULTIPLE: number = 2;

/** Minimum absolute realized PnL (D6) for the viral share to auto-prompt.
 *  $10 — keeps a "2x on $0.40" out of the celebration. */
export const VIRAL_MIN_REALIZED_PNL_D6: bigint = BigInt(10_000_000);

/** Scale applied before the single float division so the multiple keeps three
 *  fractional digits without floating-point drift. */
const RATIO_SCALE: bigint = BigInt(1000);

export interface RealizedSell {
  /** Net USDC proceeds of the sell (D6). */
  proceedsD6: bigint;
  /** Canonical checked accounting result for this exact sell. */
  accounting: CheckedPositionAccountingResult;
}

export interface ViralWin {
  /** Return multiple, e.g. 12.4 means 12.4x. */
  multiple: number;
  /** Canonical checked realized-PnL delta in D6. */
  realizedPnlD6: bigint;
  /** Display label, e.g. "12x" / "2.4x". */
  label: string;
}

/** Return multiple from a D6 ratio (numerator ÷ denominator). Returns null when
 *  the denominator is non-positive — there's no meaningful multiple to form. */
export function multipleFromD6(numeratorD6: bigint, denominatorD6: bigint): number | null {
  if (denominatorD6 <= BigInt(0)) return null;
  const scaled = (numeratorD6 * RATIO_SCALE) / denominatorD6;
  return Number(scaled) / Number(RATIO_SCALE);
}

/** Apply product return-multiple formatting to a canonical checked sell. */
export function computeRealizedWin(sell: RealizedSell): ViralWin | null {
  const multiple = multipleFromD6(sell.proceedsD6, sell.accounting.releasedCostBasisD6);
  if (multiple === null) return null;
  return {
    multiple,
    realizedPnlD6: sell.accounting.realizedPnlDeltaD6,
    label: formatMultiple(multiple),
  };
}

/** True when the realized win clears BOTH gates: the return multiple and the
 *  dollar floor. The narrowing predicate lets callers use the `ViralWin`. */
export function isViralWin(win: ViralWin | null): win is ViralWin {
  if (win === null) return false;
  return win.multiple >= VIRAL_MIN_MULTIPLE && win.realizedPnlD6 >= VIRAL_MIN_REALIZED_PNL_D6;
}

/** Format a return multiple: one decimal below 10x ("2.4x"),
 *  whole-number at or above 10x ("47x"). A clean integer never shows ".0". */
export function formatMultiple(multiple: number): string {
  if (multiple >= 10) return `${Math.floor(multiple)}x`;
  const rounded = Math.round(multiple * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}x` : `${rounded.toFixed(1)}x`;
}
